import { GeofenceEvent } from 'services/collectionGeofenceEvents';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  COLLECTION_EVENT_LABELS,
  COLLECTION_STATUS_LABELS,
  CollectionDetail,
  collectionActor,
} from 'services/CollectionsV2Service';

const M = 14;

function fmt(dateStr: string): string {
  return new Date(dateStr).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function geoText(
  lat: number | null | undefined,
  lng: number | null | undefined,
): string {
  if (lat == null || lng == null) return '—';
  return `${lat.toFixed(5)}; ${lng.toFixed(5)}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any, no-unused-vars
function geoLinkHook(doc: jsPDF, geoColIndex: number): (data: any) => void {
  return (data) => {
    if (data.section !== 'body' || data.column.index !== geoColIndex) return;
    const raw = String(data.cell.raw ?? '');
    if (raw === '—' || !raw.includes(';')) return;
    const [latStr, lngStr] = raw.split(';');
    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, {
      url: `https://www.google.com/maps?q=${lat},${lng}`,
    });
  };
}

function finalY(doc: jsPDF): number {
  return (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable
    .finalY;
}

const TABLE = {
  styles: { fontSize: 9, cellPadding: 2 },
  headStyles: {
    fillColor: [230, 230, 230] as [number, number, number],
    textColor: 0 as number,
    fontStyle: 'bold' as const,
  },
  theme: 'grid' as const,
};

const GEO_COLOR = { textColor: [22, 119, 255] as [number, number, number] };

function sectionHeader(doc: jsPDF, text: string, y: number): number {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(text, M, y);
  return y + 4;
}

function eventJourney(log: CollectionDetail['logs'][number]): string {
  const from = log.metadata?.fromJourneyId;
  if (from && log.journeyId) return `${from} → ${log.journeyId}`;
  return log.journeyId || from || '—';
}

function eventDetail(log: CollectionDetail['logs'][number]): string {
  const parts: string[] = [];
  if (log.metadata?.packageCount != null) {
    parts.push(`${log.metadata.packageCount} bultos`);
  }
  if (log.metadata?.waitedSeconds != null) {
    parts.push(`esperó ${Math.round(log.metadata.waitedSeconds / 60)} min`);
  }
  if (log.metadata?.measuredOnDevice) {
    parts.push('espera medida en el celular');
  }
  if (log.reason) parts.push(log.reason);
  return parts.join(' · ') || '—';
}

export function exportCollectionRemitoV2(
  detail: CollectionDetail,
  geofenceEvents: GeofenceEvent[] = [],
): void {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = M;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`Remito Colecta #${detail.id} — ${detail.storeName}`, M, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(
    `Creada: ${fmt(detail.createdAt)}   Estado: ${
      COLLECTION_STATUS_LABELS[detail.status] ?? detail.status
    }`,
    M,
    y,
  );
  doc.setTextColor(0, 0, 0);
  y += 8;

  y = sectionHeader(doc, 'Retiro', y);
  autoTable(doc, {
    ...TABLE,
    startY: y,
    margin: { left: M, right: M },
    head: [['Driver', 'Patente', 'Ruta', 'Declarados', 'Escaneados']],
    body: [
      [
        detail.driverName || '—',
        detail.vehiclePlate || '—',
        detail.journeyId || '—',
        detail.declaredPackageCount != null
          ? String(detail.declaredPackageCount)
          : '—',
        String(detail.intakeCodes?.length ?? 0),
      ],
    ],
  });
  y = finalY(doc) + 6;

  if (detail.depositName) {
    y = sectionHeader(doc, 'Depósito', y);
    autoTable(doc, {
      ...TABLE,
      startY: y,
      margin: { left: M, right: M },
      head: [['Nombre', 'Dirección', 'Ubicación']],
      body: [
        [
          detail.depositName,
          detail.depositAddress || '—',
          geoText(detail.depositLat, detail.depositLng),
        ],
      ],
      columnStyles: { 2: GEO_COLOR },
      didDrawCell: geoLinkHook(doc, 2),
    });
    y = finalY(doc) + 6;
  }

  y = sectionHeader(doc, 'Tienda', y);
  autoTable(doc, {
    ...TABLE,
    startY: y,
    margin: { left: M, right: M },
    head: [['Nombre', 'Marketplace', 'Dirección', 'Ubicación']],
    body: [
      [
        detail.storeName,
        detail.marketplaceName,
        detail.address || '—',
        geoText(detail.lat, detail.lng),
      ],
    ],
    columnStyles: { 3: GEO_COLOR },
    didDrawCell: geoLinkHook(doc, 3),
  });
  y = finalY(doc) + 6;

  if (detail.intakeCodes?.length) {
    y = sectionHeader(doc, 'Códigos escaneados', y);
    autoTable(doc, {
      ...TABLE,
      startY: y,
      margin: { left: M, right: M },
      head: [['Código', 'Ingresado']],
      body: detail.intakeCodes.map((code) => [
        code.externalCode,
        fmt(code.ingestedAt),
      ]),
    });
    y = finalY(doc) + 6;
  }

  const timeline = [
    ...(detail.logs ?? []).map((log) => ({
      at: log.occurredAt,
      row: [
        fmt(log.occurredAt),
        COLLECTION_EVENT_LABELS[log.event] ?? log.event,
        collectionActor(log),
        eventJourney(log),
        eventDetail(log),
      ],
    })),
    ...geofenceEvents.map((event) => ({
      at: event.occurredAt ?? '',
      row: [
        event.occurredAt ? fmt(event.occurredAt) : '—',
        event.type === 'enter' ? 'Entró al radio' : 'Salió del radio',
        'Automático',
        event.journeyId,
        [
          event.placeType === 'collection' ? 'Tienda' : 'Depósito',
          event.lat != null && event.lng != null
            ? `${event.lat.toFixed(5)}, ${event.lng.toFixed(5)}`
            : '',
          'hora del teléfono',
        ]
          .filter(Boolean)
          .join(' · '),
      ],
    })),
  ].sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''));

  if (timeline.length) {
    y = sectionHeader(doc, 'Línea de tiempo', y);
    autoTable(doc, {
      ...TABLE,
      startY: y,
      margin: { left: M, right: M },
      head: [['Fecha', 'Evento', 'Quién', 'Ruta', 'Detalle']],
      body: timeline.map((entry) => entry.row),
    });
  }

  doc.save(`remito-colecta-${detail.id}.pdf`);
}

export default exportCollectionRemitoV2;
